import React, { useEffect, useState } from "react";
import SortupDialog from "../../components/common/SortupDialog";
import { formatDateTime, formatScheduleDateTime } from "../../lib/utils";
import { DateTimePicker } from "../GoLivePage/components/DateAndTimePicker";
import { toast } from "../../@/components/ui/use-toast";
import { useOrderScheduleMutation } from "../../api/api";
import {
  CLICK_UP_TO_SCHEDULE_DELIVERY_HOURS,
  CLICK_UP_TO_SCHEDULE_DELIVERY_MINS,
} from "../../lib/constants";

interface ScheduleDeliverySectionProps {
  externalDeliveryId: string;
  isDialogOpen: boolean;
  orderId: number;
  orderDate: string;
  setIsDialogOpen: (isDialogOpen: boolean) => void;
  deliveryDate: string;
  orderApi: any;
  vendorEmail: string;
  setIsShippingLabelExpired: (isShippingLabelExpired: boolean) => void;
}

const ScheduleDeliverySection = ({
  externalDeliveryId,
  isDialogOpen,
  setIsDialogOpen,
  orderId,
  orderDate,
  deliveryDate,
  orderApi,
  vendorEmail,
  setIsShippingLabelExpired,
}: ScheduleDeliverySectionProps) => {
  const [isValidDateTime, setIsValidDateTime] = useState(true);
  const [selectedDateTime, setSelectedDateTime] = useState<string | null>(null);
  const [orderSchedule, { isLoading }] = useOrderScheduleMutation();

  const handleScheduleDelivery = async () => {
    if (selectedDateTime) {
      try {
        const date = new Date(selectedDateTime);
        const response: any = await orderSchedule({
          body: {
            readyUntil: {
              minute:
                date.getMinutes() == 0
                  ? CLICK_UP_TO_SCHEDULE_DELIVERY_MINS
                  : date.getMinutes(),
              hour: date.getHours() + CLICK_UP_TO_SCHEDULE_DELIVERY_HOURS,
            },
            readyAt: {
              minute: date.getMinutes(),
              hour: date.getHours(),
            },
            date: {
              day: date.getDate(),
              month: date.getMonth() + 1,
              year: date.getFullYear(),
            },
            externalDeliveryId,
            orderId,
            vendorEmail: vendorEmail,
            pickupDateTime: formatDateTime(
              selectedDateTime,
              "YYYY-MM-DDTHH:mm:ssZ"
            ),
          },
        });

        if (response?.data?.message.includes("successfully")) {
          toast({
            title: "Delivery scheduled successfully",
            description: response?.data?.message,
            variant: "success",
            showIcon: true,
          });
          orderApi.refetch();
          handleCloseDialog();
        } else if (response.error?.data.isShippingLabelExpired) {
          setIsShippingLabelExpired(true);
          handleCloseDialog();
          toast({
            title: "Shipping label expired",
            description: response.error?.data.message,
            variant: "destructive",
            showIcon: true,
          });
        } else {
          toast({
            title: "Error scheduling delivery",
            description: response.error?.data?.message,
            variant: "destructive",
            showIcon: true,
          });
        }
      } catch (error: any) {
        toast({
          title: "Error scheduling delivery",
          description: error?.data?.message,
          variant: "destructive",
          showIcon: true,
        });
      }
    }
  };

  const handleCloseDialog = () => {
    setSelectedDateTime(null);
    setIsValidDateTime(true);
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (selectedDateTime) {
      const selectedDate = new Date(selectedDateTime);
      const now = new Date();
      const hours = selectedDate.getHours();
      const minutes = selectedDate.getMinutes();
      const timeInMinutes = hours * 60 + minutes;
      const minTimeInMinutes = 9 * 60; // 9 AM
      const maxTimeInMinutes = 15 * 60; // 03:00 PM

      if (selectedDate <= now) {
        setIsValidDateTime(false);
        toast({
          title: "Please select a future date and time",
          description: "The selected date and time must be in the future.",
          variant: "destructive",
          showIcon: true,
        });
      } else if (
        timeInMinutes < minTimeInMinutes ||
        timeInMinutes > maxTimeInMinutes
      ) {
        setIsValidDateTime(false);
        toast({
          title: "Invalid time selection",
          description: "Please select a time between 9 AM and 03:00 PM.",
          variant: "destructive",
          showIcon: true,
        });
      } else {
        setIsValidDateTime(true);
      }
    }
  }, [selectedDateTime]);

  return (
    <div>
      <SortupDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        header="Schedule Pickup Time"
        bodyClassName="w-full min-w-[37vw]"
        footerButtons={[
          {
            name: isLoading ? "Scheduling..." : "Schedule",
            onClick: handleScheduleDelivery,
            className:
              "bg-customGreenColor text-white disabled:opacity-50 px-4 py-2 rounded-md font-medium hover:bg-opacity-90 transition-all",
            disabled: !selectedDateTime || !isValidDateTime || isLoading,
          },
          {
            name: "Close",
            onClick: handleCloseDialog,
            className:
              "bg-white text-gray-900 border border-gray-300 px-4 py-2 rounded-md font-medium hover:bg-gray-50 transition-all",
            disabled: isLoading,
          },
        ]}
      >
        <div className="flex flex-col gap-6 px-6 bg-white rounded-md">
          <div className="flex flex-col gap-4 border-b pb-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Order Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Order ID</span>
                <span className="font-medium text-gray-800">{orderId}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Order Date</span>
                <span className="font-medium text-gray-800">
                  {formatScheduleDateTime(orderDate)}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="text-sm text-gray-500">Delivery Date</span>
                <span className="font-medium text-gray-800">
                  {formatScheduleDateTime(deliveryDate)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-lg font-semibold text-gray-800">
              Select Pickup Time
            </h3>
            <span className="text-sm text-gray-500">
              Please select time between 9 AM to 03:00 PM for the Pickup
            </span>

            <div className="w-full">
              <label
                htmlFor="startDateTime"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Pickup Date and Time <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-300 rounded-md overflow-hidden">
                <DateTimePicker
                  value={
                    selectedDateTime ? new Date(selectedDateTime) : undefined
                  }
                  onValueChange={(date: Date | undefined) => {
                    const syntheticEvent = {
                      target: {
                        value: date?.toISOString() || "",
                      },
                    } as React.ChangeEvent<HTMLInputElement>;
                    setSelectedDateTime(syntheticEvent.target.value);
                  }}
                  isUseForDialog={true}
                  minTime="09:00"
                  maxTime="15:00"
                  isUseForSchedule={true}
                />
              </div>
            </div>

            {selectedDateTime && (
              <div
                className={`mt-2 p-3 rounded-md ${
                  isValidDateTime
                    ? "bg-blue-50 text-blue-700"
                    : "bg-red-50 text-red-600"
                }`}
              >
                <p className="text-sm font-medium flex items-center">
                  {isValidDateTime ? (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Your delivery pickup will be scheduled for:
                    </>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      Please select a future date and time
                    </>
                  )}
                </p>
                {isValidDateTime && (
                  <p className="text-sm font-semibold ml-7 mt-1">
                    {formatScheduleDateTime(
                      selectedDateTime,
                      CLICK_UP_TO_SCHEDULE_DELIVERY_HOURS
                    )}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </SortupDialog>
    </div>
  );
};

export default ScheduleDeliverySection;
